import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../utils/firebase-config';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import ProfileActivity, { useProfileActivity } from '../Components/ProfileActivity';

const BIO_LIMIT = 280;

/**
 * Your own profile: how other soap makers see you (name, @username, a line about you and what
 * you've shared), with one place to edit it.
 */
export default function ProfilePage() {
  const { user, userProfile, updateProfileDetails } = useAuth();
  const [publicProfile, setPublicProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({ displayName: '', username: '', bio: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);
  const activity = useProfileActivity(user?.uid);

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, 'publicProfiles', user.uid))
      .then((snap) => setPublicProfile(snap.exists() ? snap.data() : null))
      .catch((err) => console.error('Failed to load public profile:', err));
  }, [user]);

  const displayName = useMemo(
    () => publicProfile?.displayName || userProfile?.displayName || user?.displayName || 'Soap maker',
    [publicProfile, userProfile, user]
  );
  const username = publicProfile?.username || userProfile?.username || '';
  const bio = publicProfile?.bio ?? userProfile?.bio ?? '';
  const avatarUrl = publicProfile?.photoURL || userProfile?.photoURL || user?.photoURL || '';

  const startEditing = () => {
    setForm({ displayName, username, bio });
    setError('');
    setIsEditing(true);
  };

  const refreshPublicProfile = async () => {
    const snap = await getDoc(doc(db, 'publicProfiles', user.uid));
    if (snap.exists()) setPublicProfile(snap.data());
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await updateProfileDetails({ displayName: form.displayName, photoURL: avatarUrl, username: form.username, bio: form.bio });
      await refreshPublicProfile();
      setIsEditing(false);
    } catch (err) {
      setError(err?.message || 'Your profile could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Photos must be under 5 MB.');
      return;
    }
    setUploadingAvatar(true);
    setError('');
    try {
      const storageRef = ref(storage, `users/${user.uid}/avatar.${file.name.split('.').pop()}`);
      await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(storageRef);
      await updateProfileDetails({ displayName, photoURL, username });
      await refreshPublicProfile();
    } catch (err) {
      console.error('Avatar upload error:', err);
      setError('The photo could not be uploaded. Please try again.');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const copyProfileLink = async () => {
    try {
      await navigator.clipboard.writeText(`https://soaplab.in/${username}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  if (!user) {
    return (
      <div className="sl-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Profile' }]} />
          <div className="pageheading">Profile</div>
          <div className="pagedescription">Please sign in to see your profile.</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="sl-page">
      <div className="pageheader borderleft">
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Profile' }]} />
        <div className="profile-identity">
          <button
            type="button"
            className={`profile-avatar ${uploadingAvatar ? 'uploading' : ''}`}
            onClick={() => !uploadingAvatar && fileInputRef.current?.click()}
            title="Change your photo"
          >
            {avatarUrl ? <img src={avatarUrl} alt="" /> : <span>{displayName.charAt(0).toUpperCase()}</span>}
            <span className="profile-avatar-hint">{uploadingAvatar ? 'Uploading…' : 'Change'}</span>
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleAvatarChange} />
          <div>
            <h1 className="pageheading profile-name">{displayName}</h1>
            <p className="profile-handle">{username ? `@${username}` : 'No username yet: add one to get your own profile link.'}</p>
          </div>
        </div>
        {bio
          ? <p className="sl-tagline profile-bio">{bio}</p>
          : <p className="profile-muted">Add a line about the soap you make, so other members know you.</p>}
        <div className="sl-actions profile-actions">
          <button type="button" className="sl-btn sl-btn-secondary" onClick={startEditing}>Edit profile</button>
          {username && <button type="button" className="sl-arrow-link profile-link-btn" onClick={copyProfileLink}>{copied ? 'Link copied' : 'Copy profile link'}</button>}
          {username && <Link to={`/${username}`} className="sl-arrow-link">See it as others do</Link>}
        </div>
        {error && !isEditing && <p className="profile-error" role="alert">{error}</p>}
      </div>

      <div className="borderleft">
        {isEditing && (
          <section className="sl-band">
            <h2 className="sl-subtitle">Edit profile</h2>
            <form className="profile-form" onSubmit={handleSave}>
              <label className="profile-field">
                <span>Name</span>
                <input type="text" value={form.displayName} maxLength={80} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
              </label>
              <label className="profile-field">
                <span>Username</span>
                <div className="profile-username-input">
                  <span>soaplab.in/</span>
                  <input type="text" value={form.username} placeholder="yourname" maxLength={20}
                    onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })} />
                </div>
                <small>3–20 characters: lowercase letters, numbers and underscores.</small>
              </label>
              <label className="profile-field profile-field-wide">
                <span>About you</span>
                <textarea rows={3} maxLength={BIO_LIMIT} value={form.bio} placeholder="e.g. Cold process soap maker from Jaipur, selling at local markets."
                  onChange={(e) => setForm({ ...form, bio: e.target.value })} />
                <small>{form.bio.length}/{BIO_LIMIT}</small>
              </label>
              {error && <p className="profile-error profile-field-wide" role="alert">{error}</p>}
              <div className="sl-actions profile-field-wide">
                <button type="submit" className="sl-btn sl-btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save profile'}</button>
                <button type="button" className="sl-btn sl-btn-secondary" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</button>
              </div>
            </form>
          </section>
        )}
        <ProfileActivity activity={activity} isOwn name={displayName} />
      </div>
      <Footer />
    </div>
  );
}
